<template>
  <input
    type="file"
    @change="uploadBaseImage"
    accept="image/*"
    class="upload"
  />
  <canvas
    ref="canvasRef"
    class="canvas-box"
    @mousedown="startDragging"
    @mousemove="dragging"
    @mouseup="stopDragging"
    @wheel="handleWheel"
  >
  </canvas>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import axios from "axios";
import { useStore } from "vuex";
import { ElLoading, ElMessage } from "element-plus";

export default defineComponent({
  name: "CanvasImageUploader",
  props: ["imgUrl"],
  setup(props) {
    const canvasRef = ref<HTMLCanvasElement | null>(null);
    const context = ref<CanvasRenderingContext2D | null>(null);
    const baseImageRef = ref<HTMLImageElement | null>(null); // 底图
    const overlayImages = ref<
      Array<{
        // 存储多个覆盖图
        image: HTMLImageElement;
        position: { x: number; y: number };
        scale: number;
      }>
    >([]);
    const isDragging = ref(false);
    const dragStart = ref({ x: 0, y: 0 });
    const imgPosition = ref({ x: 0, y: 0 });
    const imgScale = ref(1);
    const draggingOverlayIndex = ref<number | null>(null); // 当前拖拽的覆盖图索引
    const store = useStore();
    let files = ref(null);

    onMounted(() => {
      const canvas = canvasRef.value;
      if (canvas) {
        canvas.width = 500;
        canvas.height = 500;
        context.value = canvas.getContext("2d");

        if (context.value) {
          context.value.fillStyle = "#f0f0f0";
          context.value.fillRect(0, 0, canvas.width, canvas.height);
        }
      }

      if (props.imgUrl != false) {
        customeMap2(props.imgUrl);
      }
    });

    // 上传底图
    const uploadBaseImage = (event: Event) => {
      console.log(123);
      const input = event.target as HTMLInputElement;
      if (input.files && input.files[0]) {
        const file = input.files[0];

        console.log(file);
        const reader = new FileReader();

        files.value = file;

        reader.onload = (e) => {
          console.log(e);
          const image = new Image();
          image.src = e.target?.result as string;

          image.onload = () => {
            const originalWidth = image.width;
            const originalHeight = image.height;

            if (originalHeight * 2 != originalWidth) {
              ElMessage({
                message: "图片比例必须为2:1",
                type: "warning",
              });
              return;
            }

            baseImageRef.value = image;

            // 计算图片的缩放比例，确保其适应500x500 canvas并保持比例
            const maxCanvasSize = 500;
            const widthRatio = maxCanvasSize / image.width;
            const heightRatio = maxCanvasSize / image.height;
            const scaleRatio = Math.min(widthRatio, heightRatio); // 取最小比例来填充canvas

            // 计算缩放后的图片宽高
            const scaledWidth = image.width * scaleRatio;
            const scaledHeight = image.height * scaleRatio;

            // 将图片居中显示
            imgPosition.value = {
              x: (maxCanvasSize - scaledWidth) / 2, // 水平居中
              y: (maxCanvasSize - scaledHeight) / 2, // 垂直居中
            };
            imgScale.value = scaleRatio; // 根据比例缩放图片

            drawImage(); // 重新绘制图片
          };
        };

        reader.readAsDataURL(file);
      }
    };

    // 绘制图片
    const drawImage = () => {
      const canvas = canvasRef.value;
      const ctx = context.value;
      const baseImage = baseImageRef.value;

      if (ctx && baseImage && canvas) {
        // 清除画布
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#f0f0f0";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // 绘制底图
        const scaledBaseWidth = baseImage.width * imgScale.value;
        const scaledBaseHeight = baseImage.height * imgScale.value;

        ctx.drawImage(
          baseImage,
          imgPosition.value.x,
          imgPosition.value.y,
          scaledBaseWidth,
          scaledBaseHeight
        );

        // 绘制所有覆盖图
        overlayImages.value.forEach((overlay) => {
          const scaledOverlayWidth =
            overlay.image.width * overlay.scale * imgScale.value;
          const scaledOverlayHeight =
            overlay.image.height * overlay.scale * imgScale.value;

          // 限制覆盖图不能超出底图的范围
          const maxX =
            imgPosition.value.x + scaledBaseWidth - scaledOverlayWidth;
          const maxY =
            imgPosition.value.y + scaledBaseHeight - scaledOverlayHeight;
          overlay.position.x = Math.max(
            imgPosition.value.x,
            Math.min(overlay.position.x, maxX)
          );
          overlay.position.y = Math.max(
            imgPosition.value.y,
            Math.min(overlay.position.y, maxY)
          );

          ctx.drawImage(
            overlay.image,
            overlay.position.x,
            overlay.position.y,
            scaledOverlayWidth,
            scaledOverlayHeight
          );
        });
      }
    };

    // 检测鼠标是否在某个覆盖图上
    const isMouseOnOverlayImage = (offsetX: number, offsetY: number) => {
      for (let i = overlayImages.value.length - 1; i >= 0; i--) {
        const overlay = overlayImages.value[i];
        const scaledOverlayWidth =
          overlay.image.width * overlay.scale * imgScale.value;
        const scaledOverlayHeight =
          overlay.image.height * overlay.scale * imgScale.value;
        const overlayX = overlay.position.x;
        const overlayY = overlay.position.y;

        if (
          offsetX >= overlayX &&
          offsetX <= overlayX + scaledOverlayWidth &&
          offsetY >= overlayY &&
          offsetY <= overlayY + scaledOverlayHeight
        ) {
          return i;
        }
      }
      return null;
    };

    // 开始拖拽
    const startDragging = (event: MouseEvent) => {
      const canvas = canvasRef.value;
      if (!canvas) return;

      const { offsetX, offsetY } = event;

      draggingOverlayIndex.value = isMouseOnOverlayImage(offsetX, offsetY);

      isDragging.value = true;
      dragStart.value = { x: event.clientX, y: event.clientY };
    };

    // 拖拽图片
    const dragging = (event: MouseEvent) => {
      if (!isDragging.value) return;

      const dx = event.clientX - dragStart.value.x;
      const dy = event.clientY - dragStart.value.y;

      if (draggingOverlayIndex.value !== null) {
        // 拖拽当前的覆盖图
        const overlay = overlayImages.value[draggingOverlayIndex.value];
        overlay.position.x += dx;
        overlay.position.y += dy;
      } else {
        // 移动底图时，所有覆盖图跟随移动
        imgPosition.value.x += dx;
        imgPosition.value.y += dy;

        overlayImages.value.forEach((overlay) => {
          overlay.position.x += dx;
          overlay.position.y += dy;
        });
      }

      dragStart.value = { x: event.clientX, y: event.clientY };

      drawImage();
    };

    // 停止拖拽
    const stopDragging = () => {
      isDragging.value = false;
      draggingOverlayIndex.value = null;
    };

    // 使用滚轮缩放图片
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      const canvas = canvasRef.value;
      if (!canvas) return;

      const { offsetX, offsetY } = event;

      const overlayIndex = isMouseOnOverlayImage(offsetX, offsetY);

      const scaleChange = event.deltaY > 0 ? -0.01 : 0.01;
      const previousScale = imgScale.value;
      imgScale.value = Math.max(0.1, imgScale.value + scaleChange);

      if (overlayIndex !== null) {
        // 放大/缩小选中的覆盖图
        const overlay = overlayImages.value[overlayIndex];
        const baseImage = baseImageRef.value;

        if (overlay && baseImage) {
          const scaledBaseWidth = baseImage.width * imgScale.value;
          const scaledBaseHeight = baseImage.height * imgScale.value;

          const scaledOverlayWidth =
            overlay.image.width * overlay.scale * imgScale.value;
          const scaledOverlayHeight =
            overlay.image.height * overlay.scale * imgScale.value;

          // 限制覆盖图不能超过底图的宽高
          if (
            scaledOverlayWidth <= scaledBaseWidth &&
            scaledOverlayHeight <= scaledBaseHeight
          ) {
            overlay.scale = Math.max(0.1, overlay.scale + scaleChange);
          }

          drawImage();
        }
      } else {
        // 计算底图的缩放中心
        const centerX = offsetX - imgPosition.value.x;
        const centerY = offsetY - imgPosition.value.y;

        // 缩放底图时，同时调整覆盖图位置
        overlayImages.value.forEach((overlay) => {
          const dx = overlay.position.x - imgPosition.value.x;
          const dy = overlay.position.y - imgPosition.value.y;

          // 计算缩放后的偏移量
          overlay.position.x =
            imgPosition.value.x + (dx * imgScale.value) / previousScale;
          overlay.position.y =
            imgPosition.value.y + (dy * imgScale.value) / previousScale;
        });

        // 更新底图位置，保持缩放中心不变
        imgPosition.value.x =
          offsetX - centerX * (imgScale.value / previousScale);
        imgPosition.value.y =
          offsetY - centerY * (imgScale.value / previousScale);

        drawImage();
      }
    };

    const saveImage = (gameId: string) => {
      if (files.value != null) {
        const form = new FormData();
        form.append("file", files.value);

        const options = {
          method: "POST",
          url: process.env.VUE_APP_Update_API,
          headers: { "content-type": "multipart/form-data" },
          data: form,
        };

        axios
          .request(options)
          .then((response) => {
            if (response.data.code == 200) {
              const aaa = {
                gameId: gameId,
                customizeMap: response.data.data.returnFileUrl,
                mapSelect: "自定义地图",
              };
              store.dispatch("game/UpdateGameMap", aaa).then((res) => {});
            }
          })
          .catch(function (error) {
            console.error(error);
          });
      } else if (props.imgUrl != false) {
        const aaa = {
          gameId: gameId,
          customizeMap: props.imgUrl,
          mapSelect: "自定义地图",
        };
        store.dispatch("game/UpdateGameMap", aaa).then((res) => {});
      }
    };

    const customeMap2 = (imgUrl: string) => {
      const image = new Image();
      image.src = imgUrl;

      image.onload = () => {
        baseImageRef.value = image;

        // 计算图片的缩放比例，确保其适应500x500 canvas并保持比例
        const maxCanvasSize = 500;
        const widthRatio = maxCanvasSize / image.width;
        const heightRatio = maxCanvasSize / image.height;
        const scaleRatio = Math.min(widthRatio, heightRatio); // 取最小比例来填充canvas

        // 计算缩放后的图片宽高
        const scaledWidth = image.width * scaleRatio;
        const scaledHeight = image.height * scaleRatio;

        // 将图片居中显示
        imgPosition.value = {
          x: (maxCanvasSize - scaledWidth) / 2, // 水平居中
          y: (maxCanvasSize - scaledHeight) / 2, // 垂直居中
        };

        imgScale.value = scaleRatio; // 根据比例缩放图片

        drawImage(); // 重新绘制图片
      };
    };

    return {
      canvasRef,
      uploadBaseImage,
      startDragging,
      dragging,
      stopDragging,
      handleWheel,
      saveImage,
      customeMap2,
    };
  },
});
</script>

<style scoped lang="less">
.canvas-box {
  width: 100%;
  height: 100%;
}

.upload {
}
</style>
